class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');

    if (this.mainDetailsToggle) {
      this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
      this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
      this.content = this.mainDetailsToggle.querySelector('summary');
    }

    if (this.content) {
      this.content = this.content.nextElementSibling;
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');

    // Check if this is a mega menu (no details element) or standard dropdown
    this.isMegaMenu = !this.querySelector('details');

    if (this.isMegaMenu) {
      // For mega menu structure (summary + div, no details)
      this.summaryElement = this.querySelector('summary');
      this.contentElement = this.querySelector('.mega-menu__content');
      this.isOpen = false;
      this.parentLi = this.closest('li');

      if (this.summaryElement && this.parentLi) {
        // Add hover functionality to parent li to prevent menu closing when moving between elements
        this.parentLi.addEventListener('mouseenter', this.openMegaMenu.bind(this));
        this.parentLi.addEventListener('mouseleave', this.closeMegaMenu.bind(this));
        this.addEventListener('focusout', this.onFocusOut.bind(this));
      }
    }
  }

  openMegaMenu() {
    if (this.isMegaMenu && !this.isOpen) {
      this.isOpen = true;
      this.setAttribute('open', '');
      this.preventBackgroundScroll();
      
      // Prevent header from hiding
      if (this.header) {
        this.header.preventHide = true;
      }
    }
  }

  closeMegaMenu() {
    if (this.isMegaMenu && this.isOpen) {
      this.isOpen = false;
      this.removeAttribute('open');
      this.allowBackgroundScroll();
      
      // Allow header to hide again if no other menus are open
      if (this.header && !this.hasOtherOpenMenus()) {
        this.header.preventHide = false;
      }
    }
  }

  hasOtherOpenMenus() {
    const allHeaderMenus = document.querySelectorAll('header-menu');
    return Array.from(allHeaderMenus).some(menu => 
      menu !== this && (
        (menu.isMegaMenu && menu.isOpen) ||
        (!menu.isMegaMenu && menu.mainDetailsToggle && menu.mainDetailsToggle.hasAttribute('open'))
      )
    );
  }

  onToggle() {
    // Handle standard dropdown menus (with details element)
    if (!this.isMegaMenu) {
      if (!this.header) return;
      this.header.preventHide = this.mainDetailsToggle.open;

      // Handle background scroll prevention
      if (this.mainDetailsToggle.open) {
        this.preventBackgroundScroll();
      } else {
        this.allowBackgroundScroll();
      }

      if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
      );
    }
  }

  preventBackgroundScroll() {
    // Add overflow-hidden if at least one menu is open and not already set
    if (!document.body.classList.contains('overflow-hidden')) {
      document.body.classList.add('overflow-hidden');
    }
  }
  allowBackgroundScroll() {
    // Remove overflow-hidden only if no header menus are open
    const allHeaderMenus = document.querySelectorAll('header-menu');
    const anyOpen = Array.from(allHeaderMenus).some(menu =>
      (menu.isMegaMenu && menu.isOpen) ||
      (!menu.isMegaMenu && menu.mainDetailsToggle && menu.mainDetailsToggle.open)
    );
    if (!anyOpen) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  close() {
    if (this.isMegaMenu) {
      this.closeMegaMenu();
    } else {
      // Check if the menu was open before closing to avoid double-counting
      const wasOpen = this.mainDetailsToggle && this.mainDetailsToggle.open;
      if (this.mainDetailsToggle) {
        super.close();
      }
      
      // If the menu was open and we're closing it programmatically,
      // ensure background scroll is restored in case toggle event doesn't fire
      if (wasOpen) {
        this.allowBackgroundScroll();
      }
    }
  }

  onFocusOut() {
    if (this.isMegaMenu) {
      setTimeout(() => {
        // Check if focus is still within the parent li or header-menu
        const focusWithinParent = this.parentLi && this.parentLi.contains(document.activeElement);
        const focusWithinMenu = this.contains(document.activeElement);
        if (!focusWithinParent && !focusWithinMenu) {
          this.closeMegaMenu();
        }
      });
    } else {
      super.onFocusOut();
    }
  }
}

customElements.define('header-menu', HeaderMenu);
